"use client";

import { useEffect } from "react";
import { Facebook, Instagram, ArrowUpRight } from "lucide-react";
import APP_CONFIG from "@/config/app.config";
import { FigmaSectionTitle } from "./FigmaSectionTitle";

const cleanUrl = (value) =>
  typeof value === "string" && value.trim() && value.trim() !== "#"
    ? value.trim()
    : "";

const INSTAGRAM_EMBED_SCRIPT_ID = "instagram-embed-script";

// Instagram blocks plain <iframe src=".../embed/"> with X-Frame-Options: DENY.
// Posts must be embedded via the official embed.js, which upgrades
// <blockquote class="instagram-media"> elements into authorized iframes.
const useInstagramEmbedScript = (enabled) => {
  useEffect(() => {
    if (!enabled) return;
    const processEmbeds = () => window.instgrm?.Embeds?.process();
    const existing = document.getElementById(INSTAGRAM_EMBED_SCRIPT_ID);
    if (existing) {
      processEmbeds();
      return;
    }
    const script = document.createElement("script");
    script.id = INSTAGRAM_EMBED_SCRIPT_ID;
    script.src = "https://www.instagram.com/embed.js";
    script.async = true;
    script.onload = processEmbeds;
    document.body.appendChild(script);
  }, [enabled]);
};

export const SocialFeedSection = () => {
  const social = APP_CONFIG.site?.social || {};
  const feed = social.feed || {};
  const enabled = feed.enabled !== false;

  const instagramPosts = (Array.isArray(feed.instagramPosts) ? feed.instagramPosts : [])
    .map(cleanUrl)
    .filter(Boolean)
    .slice(0, 6);

  useInstagramEmbedScript(enabled && instagramPosts.length > 0);

  if (!enabled) return null;

  const instagramHandle = feed.instagramHandle || "";
  const instagramUrl =
    cleanUrl(social.instagram) ||
    (instagramHandle ? `https://www.instagram.com/${instagramHandle}/` : "");

  const facebookEmbedUrl = cleanUrl(feed.facebookPageUrl);
  const facebookUrl =
    facebookEmbedUrl ||
    cleanUrl(social.facebook) ||
    (feed.facebookHandle ? `https://www.facebook.com/${feed.facebookHandle}` : "");
  const facebookTimelineSrc = facebookEmbedUrl
    ? `https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(
        facebookEmbedUrl,
      )}&tabs=timeline&small_header=true&adapt_container_width=true&hide_cover=false&show_facepile=false&width=500&height=420`
    : "";

  if (!instagramUrl && !facebookUrl) return null;

  return (
    <section
      className="w-full px-6 sm:px-10 lg:px-16 xl:px-24 2xl:px-32"
      style={{ contentVisibility: "auto", containIntrinsicSize: "520px" }}
    >
      <div className="space-y-5">
        <FigmaSectionTitle
          title="Follow Us"
          subtitle="Parts drops, fitment tips and workshop stories — find us on Instagram and Facebook."
        />

        <div className="grid gap-4 lg:grid-cols-2">
          {/* Instagram panel */}
          {instagramUrl ? (
            <div className="relative overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950 p-6 text-white sm:p-8">
              <div className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-linear-to-br from-amber-400/30 via-pink-500/20 to-purple-600/30 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-20 -left-10 h-40 w-40 rounded-full bg-purple-600/20 blur-3xl" />

              <div className="relative flex h-full flex-col gap-5">
                <div className="flex items-center gap-4">
                  <div className="rounded-2xl bg-linear-to-br from-amber-400 via-pink-500 to-purple-600 p-0.5">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-950">
                      <Instagram className="h-6 w-6" />
                    </div>
                  </div>
                  <div>
                    <p className="font-display text-lg font-bold uppercase tracking-wide">
                      Instagram
                    </p>
                    {instagramHandle ? (
                      <p className="text-sm text-zinc-400">@{instagramHandle}</p>
                    ) : null}
                  </div>
                </div>

                {instagramPosts.length > 0 ? (
                  <div className="grid items-start gap-3 sm:grid-cols-2">
                    {instagramPosts.map((postUrl) => (
                      <blockquote
                        key={postUrl}
                        className="instagram-media m-0! w-full overflow-hidden rounded-xl bg-white"
                        data-instgrm-permalink={postUrl}
                        data-instgrm-version="14"
                      >
                        <a
                          href={postUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block p-4 text-sm text-zinc-600"
                        >
                          View this post on Instagram
                        </a>
                      </blockquote>
                    ))}
                  </div>
                ) : (
                  <p className="max-w-md text-sm leading-relaxed text-zinc-300">
                    New part drops, garage stories and fitment tips — straight
                    from our workshop to your feed.
                  </p>
                )}

                <a
                  href={instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-auto inline-flex w-fit items-center gap-2 rounded-full bg-linear-to-r from-amber-400 via-pink-500 to-purple-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition-transform hover:scale-105"
                >
                  Follow on Instagram
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          ) : null}

          {/* Facebook panel */}
          {facebookUrl ? (
            <div className="relative overflow-hidden rounded-2xl border border-zinc-200 bg-white p-6 sm:p-8">
              <div className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[#1877F2]/10 blur-3xl" />

              <div className="relative flex h-full flex-col gap-5">
                <div className="flex items-center gap-4">
                  <div className="flex h-13 w-13 items-center justify-center rounded-2xl bg-[#1877F2] text-white shadow-sm">
                    <Facebook className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="font-display text-lg font-bold uppercase tracking-wide text-zinc-900">
                      Facebook
                    </p>
                    <p className="text-sm text-zinc-500">
                      {APP_CONFIG.site?.name || "Car Spares"}
                    </p>
                  </div>
                </div>

                {facebookTimelineSrc ? (
                  <iframe
                    src={facebookTimelineSrc}
                    title="Facebook page timeline"
                    loading="lazy"
                    scrolling="no"
                    allow="encrypted-media"
                    className="h-105 w-full rounded-xl border-0"
                  />
                ) : (
                  <p className="max-w-md text-sm leading-relaxed text-zinc-600">
                    Product news, seasonal offers and quick support — join the
                    conversation with fellow car owners and workshops.
                  </p>
                )}

                <a
                  href={facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-auto inline-flex w-fit items-center gap-2 rounded-full bg-[#1877F2] px-5 py-2.5 text-sm font-bold text-white shadow-md transition-transform hover:scale-105"
                >
                  Follow on Facebook
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};
